import type { SVGProps } from 'react';

/**
 * Functional icons only, drawn as hairline strokes at the same weight as the
 * rules on the page. There is no decorative vector art in this file and no
 * gradient-filled logo mark — the brand is a wordmark set in the display
 * serif with a registration flash beside it (see AppShell).
 */
type IconProps = SVGProps<SVGSVGElement> & { size?: number };

function Icon({ size = 20, children, ...rest }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      {...rest}
    >
      {children}
    </svg>
  );
}

export const IconRack = (p: IconProps) => (
  <Icon {...p}>
    <path d="M4 7h16" />
    <path d="M8 7v3a4 4 0 0 0 8 0V7" />
    <path d="M12 3v4" />
    <path d="M6 21l2-8h8l2 8z" />
  </Icon>
);

export const IconStudio = (p: IconProps) => (
  <Icon {...p}>
    <circle cx="12" cy="12" r="3.2" />
    <path d="M4 8.5 8 4h8l4 4.5V19a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1z" />
  </Icon>
);

export const IconLooks = (p: IconProps) => (
  <Icon {...p}>
    <rect x="3" y="3" width="7" height="10" rx="1.6" />
    <rect x="14" y="3" width="7" height="6" rx="1.6" />
    <rect x="3" y="16" width="7" height="5" rx="1.6" />
    <rect x="14" y="12" width="7" height="9" rx="1.6" />
  </Icon>
);

export const IconRoom = (p: IconProps) => (
  <Icon {...p}>
    <circle cx="9" cy="9" r="3" />
    <path d="M3 20a6 6 0 0 1 12 0" />
    <circle cx="17.5" cy="7.5" r="2.2" />
    <path d="M15.5 14.2A5 5 0 0 1 21 19" />
  </Icon>
);

export const IconRunway = (p: IconProps) => (
  <Icon {...p}>
    <path d="M9 3h6l-1.5 18h-3z" />
    <path d="M4 21h16" />
    <path d="M7.5 8h9" />
  </Icon>
);

export const IconSavant = (p: IconProps) => (
  <Icon {...p}>
    <path d="M12 3.5 13.6 8l4.5 1.6L13.6 11l-1.6 4.5L10.4 11 6 9.6 10.4 8z" />
    <path d="M18 16.5l.7 1.9 1.9.7-1.9.7-.7 1.9-.7-1.9-1.9-.7 1.9-.7z" />
  </Icon>
);

export const IconUser = (p: IconProps) => (
  <Icon {...p}>
    <circle cx="12" cy="8.5" r="3.6" />
    <path d="M4.5 20.5a7.5 7.5 0 0 1 15 0" />
  </Icon>
);

export const IconBell = (p: IconProps) => (
  <Icon {...p}>
    <path d="M6 9a6 6 0 0 1 12 0c0 4 1.5 5.5 1.5 5.5h-15S6 13 6 9z" />
    <path d="M10 18a2 2 0 0 0 4 0" />
  </Icon>
);

export const IconPlus = (p: IconProps) => (
  <Icon {...p}>
    <path d="M12 5v14M5 12h14" />
  </Icon>
);

export const IconClose = (p: IconProps) => (
  <Icon {...p}>
    <path d="M6 6l12 12M18 6 6 18" />
  </Icon>
);

export const IconCheck = (p: IconProps) => (
  <Icon {...p}>
    <path d="M4.5 12.5 9.5 17.5 19.5 6.5" />
  </Icon>
);

export const IconChevronDown = (p: IconProps) => (
  <Icon {...p}>
    <path d="m6 9 6 6 6-6" />
  </Icon>
);

export const IconChevronLeft = (p: IconProps) => (
  <Icon {...p}>
    <path d="m15 5-7 7 7 7" />
  </Icon>
);

export const IconChevronRight = (p: IconProps) => (
  <Icon {...p}>
    <path d="m9 5 7 7-7 7" />
  </Icon>
);

export const IconSearch = (p: IconProps) => (
  <Icon {...p}>
    <circle cx="11" cy="11" r="6.5" />
    <path d="m16 16 4.5 4.5" />
  </Icon>
);

export const IconShare = (p: IconProps) => (
  <Icon {...p}>
    <path d="M12 15V4" />
    <path d="m8 7.5 4-4 4 4" />
    <path d="M5 13v6a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-6" />
  </Icon>
);

export const IconTrash = (p: IconProps) => (
  <Icon {...p}>
    <path d="M4 7h16" />
    <path d="M10 4h4" />
    <path d="M6.5 7 7 20h10l.5-13" />
    <path d="M10.5 10.5v6M13.5 10.5v6" />
  </Icon>
);

export const IconBookmark = (p: IconProps) => (
  <Icon {...p}>
    <path d="M6.5 3.5h11a1 1 0 0 1 1 1v16l-6.5-4-6.5 4v-16a1 1 0 0 1 1-1z" />
  </Icon>
);

export const IconSparkle = (p: IconProps) => (
  <Icon {...p}>
    <path d="M12 4v16M4 12h16" />
    <path d="m7 7 10 10M17 7 7 17" opacity="0.45" />
  </Icon>
);

export const IconRemix = (p: IconProps) => (
  <Icon {...p}>
    <path d="M4 7h5l6 10h5" />
    <path d="m17 3 3 4-3 4" />
    <path d="M4 17h5" />
  </Icon>
);

export const IconCopy = (p: IconProps) => (
  <Icon {...p}>
    <rect x="9" y="9" width="11" height="11" rx="2" />
    <path d="M5 15V6a2 2 0 0 1 2-2h9" />
  </Icon>
);

export const IconCamera = (p: IconProps) => (
  <Icon {...p}>
    <path d="M4 8h3l1.5-2h7L17 8h3a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9a1 1 0 0 1 1-1z" />
    <circle cx="12" cy="13.5" r="3.4" />
  </Icon>
);

export const IconSend = (p: IconProps) => (
  <Icon {...p}>
    <path d="M4.5 12 20 4.5 15 20l-3.5-6.5z" />
    <path d="m11.5 13.5 8.5-9" />
  </Icon>
);

export const IconLock = (p: IconProps) => (
  <Icon {...p}>
    <rect x="4.5" y="10" width="15" height="10" rx="2" />
    <path d="M8 10V7.5a4 4 0 0 1 8 0V10" />
  </Icon>
);

export const IconMail = (p: IconProps) => (
  <Icon {...p}>
    <rect x="3" y="5.5" width="18" height="13" rx="2.5" />
    <path d="M3.8 7l7.3 5.4a1.5 1.5 0 0 0 1.8 0L20.2 7" />
  </Icon>
);

export const IconKey = (p: IconProps) => (
  <Icon {...p}>
    <circle cx="8" cy="12" r="4" />
    <path d="M12 12h9" />
    <path d="M17.5 12v3" />
    <path d="M20.5 12v2" />
  </Icon>
);

export const IconGlobe = (p: IconProps) => (
  <Icon {...p}>
    <circle cx="12" cy="12" r="8.5" />
    <path d="M3.5 12h17" />
    <path d="M12 3.5c2.4 2.4 3.6 5.3 3.6 8.5S14.4 18.1 12 20.5c-2.4-2.4-3.6-5.3-3.6-8.5S9.6 5.9 12 3.5z" />
  </Icon>
);

export const IconMenu = (p: IconProps) => (
  <Icon {...p}>
    <path d="M4 7h16M4 12h16M4 17h16" />
  </Icon>
);

export const IconArrowUp = (p: IconProps) => (
  <Icon {...p}>
    <path d="M12 19V5" />
    <path d="m6 11 6-6 6 6" />
  </Icon>
);

export const IconArrowDown = (p: IconProps) => (
  <Icon {...p}>
    <path d="M12 5v14" />
    <path d="m6 13 6 6 6-6" />
  </Icon>
);

export const IconRegistration = (p: IconProps) => (
  <Icon {...p}>
    <circle cx="12" cy="12" r="6" />
    <path d="M12 2v6M12 16v6M2 12h6M16 12h6" />
  </Icon>
);
